import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import { InView } from 'react-intersection-observer';
import useMedia from '../_internal/useMedia';
import Box from '../Box';
import IconButton from '../IconButton';
import Placeholder from '../Placeholder';
import IconChevronUp16 from '../icons/IconChevronUp16';
import IconChevronDown16 from '../icons/IconChevronDown16';

import styles from './thumbnailNavigation.module.scss';

interface ThumbnailNavigationProps {
  thumbnails: React.ReactNode[];
  selectedSlide: number;
  setSelectedSlide: (index: number) => void;
}

export const PREVIOUS_LABEL = 'Scroll to previous thumbnails';
export const NEXT_LABEL = 'Scroll to next thumbnails';

const ThumbnailNavigation = ({
  thumbnails,
  selectedSlide,
  setSelectedSlide,
}: ThumbnailNavigationProps) => {
  const [showPreviousControls, setShowPreviousControls] = useState(false);
  const [showNextControls, setShowNextControls] = useState(false);
  const thumbnailContainerRef = useRef<HTMLDivElement>(null);
  const isLoading = thumbnails.length === 0;

  const prefersReducedMotion = useMedia('(prefers-reduced-motion)');
  const thumbnailContainer = thumbnailContainerRef?.current;

  const handleScroll = (direction: 'prev' | 'next') => {
    if (thumbnailContainer) {
      const scrollBy = thumbnailContainer.clientHeight / 2;

      thumbnailContainer.scrollBy({
        top: direction === 'prev' ? -scrollBy : scrollBy,
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      });
    }
  };

  const handleControlVisibility = (index: number, inView: boolean) => {
    if (index === 0) {
      setShowPreviousControls(!inView);
    } else if (index === thumbnails.length - 1) {
      setShowNextControls(!inView);
    }
  };

  const thumbnailElements = thumbnails.map((thumbnail, index) => {
    return (
      <InView
        // eslint-disable-next-line react/no-array-index-key
        key={index}
        data-testid="thumbnail-wrapper"
        threshold={1}
        onChange={inView => handleControlVisibility(index, inView)}
        root={thumbnailContainer}
        as="button"
        onClick={() => setSelectedSlide(index)}
        className={classNames(styles.thumbnail, {
          [styles.selected]: index === selectedSlide,
        })}
      >
        <Box className={styles['thumbnail-content']}>{thumbnail}</Box>
      </InView>
    );
  });

  return (
    <div className={styles.thumbnails} data-testid="thumbnails">
      {showPreviousControls && !isLoading && (
        <Box className={`${styles['thumbnail-control']} ${styles.previous}`}>
          <IconButton
            variant="knockout"
            aria-label={PREVIOUS_LABEL}
            onClick={() => handleScroll('prev')}
          >
            <IconChevronUp16 />
          </IconButton>
        </Box>
      )}
      <Box ref={thumbnailContainerRef} className={styles['thumbnail-elements']}>
        {isLoading
          ? Array.from({ length: 3 }, (_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Box
                key={index}
                className={styles.thumbnail}
                data-testid="thumbnail-placeholder"
              >
                <Box className={styles['thumbnail-content']}>
                  <Placeholder width="100%" height="100%" variant="rect" />
                </Box>
              </Box>
            ))
          : thumbnailElements}
      </Box>
      {showNextControls && !isLoading && (
        <Box className={`${styles['thumbnail-control']} ${styles.next}`}>
          <IconButton
            variant="knockout"
            aria-label={NEXT_LABEL}
            onClick={() => handleScroll('next')}
          >
            <IconChevronDown16 />
          </IconButton>
        </Box>
      )}
    </div>
  );
};

export default ThumbnailNavigation;
